'use client';

import { useState } from 'react';
import INewsPage, { INewsResponse } from '@/models/news';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { format } from 'date-fns';
import Content from '@/components/Content';
import FrequentlyAskedQuestions from '@/components/FrequentlyAskedQuestions';
import Header from '@/components/Header';
import Pagination from '@/components/Pagination';
import ContactCTA from '@/components/ContactCTA';
import ContactNew from '@/components/ContactNew';
import { newsHref } from '@/lib/routes';

export default function BlogPage(props: {
	newsPage: INewsPage;
	featuredBlog: INewsResponse;
	blogs: INewsResponse;
}) {
	'use client';

	const newsPage = props.newsPage;

	const [pageNum, setPageNum] = useState(0);
	const perPage = 8;

	const { blogs } = props;
	const paginatedBlogs = blogs?.items?.slice(pageNum * perPage, (pageNum + 1) * perPage);

	return (
		<section className="-mt-16">
			<div className="z-20 relative bg-contain xl:bg-cover bg-white text-white bg-bottom  flex flex-col">
				<Header
					small
					title={newsPage?.title}
					headerSliderImagesCollection={{ items: [newsPage?.image] }}
				/>
			</div>

			<div className="relative black py-12 md:pb-16 bg-transparent flex flex-col gap:14 md:gap-28 bg-white">
				<Content
					className="[&_h2]:text-3xl [&_h2]:sm:text-4xl [&_h2]:md:text-5xl border-b-2 pb-16 lg:pb-32 lg:pt-10"
					xlTitle="Featured news"
					title={props.featuredBlog?.items?.[0]?.title}
					image={props.featuredBlog?.items?.[0]?.coverImage}
					caption={format(
						new Date(props.featuredBlog?.items?.[0]?.date ?? new Date()),
						'MMMM do, yyyy'
					)}
					imageCover="cover"
					buttons={[
						{
							link: newsHref(props?.featuredBlog?.items?.[0]),
							text: 'Discover more',
							color: 'dark',
							variant: 'outlined',
						},
					]}
				/>

				<div className="flex flex-col gap-12 md:gap-32  overflow-hidden mt-16 lg:mt-0">
					{paginatedBlogs?.map((blog, index) => {
						return (
							<Content
								key={blog.slug}
								description={blog.excerpt}
								title={blog?.title}
								image={blog?.coverImage}
								buttons={[
									{
										link: newsHref(blog),
										text: 'Discover More',
										color: 'dark',
										variant: 'outlined',
									},
								]}
								caption={format(new Date(blog?.date), 'MMMM do, yyyy')}
								imageCover="cover"
								reverse={index % 2 === 0}
							/>
						);
					})}
				</div>
			</div>

			<div className="pb-16 md:pb-32 bg-white flex">
				<Pagination
					defaultPerPage={perPage}
					currentPage={pageNum}
					totalItems={props.blogs?.meta?.total ?? 0}
					onPageChange={(page) => {
						setPageNum(page);
						window.scrollTo({ top: 0, behavior: 'smooth' });
					}}
				/>
			</div>

			{!!newsPage?.faqsCollection?.items?.length ? (
				<div className="z-10 relative py-12  md:pb-32 flex flex-col overflow-clip bg-white">
					<FrequentlyAskedQuestions
						faqs={newsPage?.faqsCollection?.items}
						title={'Frequently Asked Questions'}
					/>
				</div>
			) : null}

			<ContactCTA />
		</section>
	);
}
